import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractApiErrorMessage } from '@/lib/api-client';
import { useUpdateTransferReferenceMutation } from '../api/use-mark-raised-mutation';
import type { InvoiceRow } from '../types';

export interface EditTransferReferenceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly currentValue: string | null;
  readonly onSaved?: (() => void) | undefined;
}

// Provides/edits the Airwallex transfer id on a raised payable invoice without settling it.
export function EditTransferReferenceDialog(props: EditTransferReferenceDialogProps) {
  const { open, onOpenChange, invoice, currentValue, onSaved } = props;
  const [transferId, setTransferId] = useState('');

  useEffect(() => {
    if (open) setTransferId(currentValue ?? '');
  }, [open, currentValue]);

  const mutation = useUpdateTransferReferenceMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });

  const submit = () => {
    const trimmed = transferId.trim();
    mutation.mutate(
      { airwallex_transfer_id: trimmed === '' ? null : trimmed },
      {
        onSuccess: () => {
          toast.success('Transfer reference saved.');
          onSaved?.();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(extractApiErrorMessage(err, 'Failed to save the transfer reference.'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Airwallex Transfer ID</DialogTitle>
          <DialogDescription>
            Record the Airwallex transfer id for this raised payment. This does not change the
            invoice payment status.
          </DialogDescription>
        </DialogHeader>
        <TransferReferenceForm
          transferId={transferId}
          setTransferId={setTransferId}
          isPending={mutation.isPending}
          onCancel={() => {
            onOpenChange(false);
          }}
          onSubmit={submit}
        />
      </DialogContent>
    </Dialog>
  );
}

function TransferReferenceForm({
  transferId,
  setTransferId,
  isPending,
  onCancel,
  onSubmit,
}: Readonly<{
  transferId: string;
  setTransferId: (value: string) => void;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}>) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-3"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="edit-transfer-id">Transfer ID</Label>
        <Input
          id="edit-transfer-id"
          data-testid="edit-transfer-id"
          value={transferId}
          onChange={(e) => {
            setTransferId(e.target.value);
          }}
          maxLength={256}
          disabled={isPending}
        />
        <p className="text-muted-foreground text-xs">Leave empty to clear the reference.</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" data-testid="edit-transfer-id-confirm" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
